import { buildApiUrl } from "@crewledger/shared/utils";
import { getE2eRoleHeader } from "@/features/auth/e2e-auth-provider";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

let tokenGetter: (() => Promise<string | null>) | null = null;

export function setApiTokenGetter(getter: () => Promise<string | null>): void {
  tokenGetter = getter;
}

function buildRequestUrl(path: string): string {
  return API_BASE ? buildApiUrl(API_BASE, path) : path;
}

async function authHeaders(initHeaders?: HeadersInit): Promise<HeadersInit> {
  const token = tokenGetter ? await tokenGetter() : null;
  const e2eRole = getE2eRoleHeader();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(e2eRole ? { "X-Werkio-E2E-User": e2eRole } : {}),
    ...initHeaders,
  };
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type") ?? "";
  const body = await response.text();

  if (!contentType.includes("application/json")) {
    if (body.trimStart().toLowerCase().startsWith("<!doctype") || body.trimStart().startsWith("<html")) {
      throw new Error(
        "API returned a web page instead of JSON. Check that VITE_API_URL points at the Worker.",
      );
    }
    throw new Error(
      body
        ? `Unexpected non-JSON response (${response.status}).`
        : `Request failed: ${response.status}`,
    );
  }

  try {
    return JSON.parse(body) as T;
  } catch {
    throw new Error(`Invalid JSON response (${response.status}).`);
  }
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(buildRequestUrl(path), {
    ...init,
    headers: await authHeaders({
      "Content-Type": "application/json",
      ...init?.headers,
    }),
  });

  if (!response.ok) {
    try {
      const error = await parseJsonResponse<{
        error?: { code?: string; message?: string };
      }>(response);
      throw new Error(error.error?.message ?? `Request failed: ${response.status}`);
    } catch (error) {
      if (error instanceof Error && error.message !== `Request failed: ${response.status}`) {
        throw error;
      }
      throw new Error(`Request failed: ${response.status}`);
    }
  }

  return parseJsonResponse<T>(response);
}

/** Parse `filename="..."` from a Content-Disposition header. */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""));
    } catch {
      return utf8Match[1].trim().replace(/^"|"$/g, "");
    }
  }
  const match = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(header);
  const raw = match?.[1] ?? match?.[2];
  return raw?.trim().replace(/^"|"$/g, "") || null;
}

/**
 * Download a binary/CSV/PDF API response with auth.
 * Avoids `<a href="/api/...">` which hits the Pages SPA and saves as .htm.
 */
export async function downloadAuthenticatedFile(
  path: string,
  fallbackFileName: string,
  requestHeaders?: HeadersInit,
): Promise<void> {
  const response = await fetch(buildRequestUrl(path), {
    headers: await authHeaders(requestHeaders),
  });

  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as {
      error?: { message?: string };
    };
    throw new Error(error.error?.message ?? `Download failed: ${response.status}`);
  }

  const blob = await response.blob();
  const fileName =
    parseContentDispositionFilename(response.headers.get("Content-Disposition")) ??
    fallbackFileName;
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Open a protected binary response from an authenticated API in a new tab. */
export async function viewAuthenticatedFile(path: string): Promise<void> {
  const response = await fetch(buildRequestUrl(path), {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as {
      error?: { message?: string };
    };
    throw new Error(error.error?.message ?? `View failed: ${response.status}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (!opened) {
    URL.revokeObjectURL(objectUrl);
    throw new Error("Pop-up blocked. Allow pop-ups to view this document.");
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export async function uploadAuthenticatedFormData<T>(
  path: string,
  formData: FormData,
): Promise<T> {
  const response = await fetch(buildRequestUrl(path), {
    method: "POST",
    headers: await authHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as {
      error?: { message?: string };
    };
    throw new Error(error.error?.message ?? `Upload failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export function getFeatureFlags() {
  return {
    onchainProofs: import.meta.env.VITE_ENABLE_ONCHAIN_PROOFS === "true",
  };
}
